import { useMemo, type ReactNode } from 'react';
import { Avatar, Button, Input, Typography } from 'antd';
import { IconX } from '@tabler/icons-react';

import { initialsFromDisplayName } from '@/shared/lib/displayName';

import s from './RecordCommentsSection.module.scss';

const { Text } = Typography;

export type RecordCommentItem = {
  id: string;
  authorName: string;
  authorRole?: string | null;
  createdAt: string;
  body: string;
  edited?: boolean;
};

function renderBodyWithMentions(text: string): ReactNode {
  const out: ReactNode[] = [];
  const re = /@([^@\n"]+)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      out.push(<span key={`t${key++}`}>{text.slice(last, m.index)}</span>);
    }
    out.push(
      <span key={`m${key++}`} className={s.mention}>
        {m[0]}
      </span>,
    );
    last = re.lastIndex;
  }
  if (last < text.length) {
    out.push(<span key={`t-end-${last}`}>{text.slice(last)}</span>);
  }
  return out.length ? out : text;
}

type RecordCommentsSectionProps = {
  items: RecordCommentItem[];
  /** Имя текущего пользователя для инициалов в поле ввода */
  composerDisplayName?: string | null;
  showComposer?: boolean;
  composerDisabled?: boolean;
  onAttach?: () => void;
  onMention?: () => void;
  onSend?: () => void;
};

export function RecordCommentsSection({
  items,
  composerDisplayName,
  showComposer = true,
  composerDisabled = true,
  onAttach,
  onMention,
  onSend,
}: RecordCommentsSectionProps) {
  const composerInitials = useMemo(() => {
    const n = composerDisplayName?.trim();
    if (!n) return '?';
    return initialsFromDisplayName(n);
  }, [composerDisplayName]);

  const title = `Комментарии · ${items.length}`;

  return (
    <div className={s.root}>
      <div className={s.root__title}>{title}</div>

      {showComposer ? (
        <div className={s.root__composer}>
          <Avatar size={36} className={s.avatar}>
            {composerInitials}
          </Avatar>
          <div className={s.root__composerMain}>
            <Input placeholder="Написать комментарий..." disabled={composerDisabled} className={s.root__input} />
            <div className={s.root__actions}>
              <div className={s.root__links}>
                <Button type="link" className={s.root__linkBtn} onClick={onAttach}>
                  Прикрепить
                </Button>
                <Button type="link" className={s.root__linkBtn} onClick={onMention}>
                  Упомянуть
                </Button>
              </div>
              <Button type="primary" size="small" className={s.root__sendBtn} disabled onClick={onSend}>
                Отправить
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {items.length > 0 ? (
        <ul
          className={[s.root__list, showComposer && items.length > 0 ? s.root__list_sep : ''].filter(Boolean).join(' ')}
        >
          {items.map((c) => (
            <li key={c.id} className={s.root__item}>
              <Avatar size={36} className={s.avatar}>
                {initialsFromDisplayName(c.authorName)}
              </Avatar>
              <div className={s.root__itemBody}>
                <div className={s.root__header}>
                  <div className={s.root__headerLeft}>
                    <span className={s.root__name}>{c.authorName}</span>
                    {c.authorRole ? <span className={s.root__role}>({c.authorRole})</span> : null}
                  </div>
                  <span className={s.root__time}>
                    {new Date(c.createdAt).toLocaleString('ru-RU')}
                    {c.edited ? ' (ред.)' : ''}
                  </span>
                </div>
                <div className={s.root__message}>{renderBodyWithMentions(c.body)}</div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className={s.root__empty}>
          <IconX size={16} style={{ color: 'var(--n400)' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Пока никто не оставил комментарий
          </Text>
        </div>
      )}
    </div>
  );
}
